import {
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Alert, Dialog, Switch, Button } from '@theguarantors/tg-package-material-ui';
import { checkIfPmi } from '@/utils/commonUtils';
import { useCurrentUser } from '@/stores/useCurrentUser';
import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';
import {
  Control,
  Controller,
  FieldErrors,
  useWatch,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupRadioButtonV2, {
  GroupRadioButtonV2Skeleton,
  type RadioInputBox,
} from '../../../components/input/GroupRadioButtonV2';
import { theme } from '@theguarantors/tg-package-material-ui';
import { Renter } from '../../../utils/types/renter.interface';
import {
  staticInstitutionalScreeningResultsOptions,
  staticSmbScreeningResultsOptions,
} from '../screening-results.options';
import { useDynamicCoverageRules } from '@/shared/hooks';
import { buildEmptyOptions, resolveDynamicCoverageOptions } from '../utils/coverage.util';
import { InfoBanner } from '@/components/InfoBanner';
import {
  useInviteTracking,
  useTrackCoveragePricingResolved,
  useTrackScreeningLabelsLoaded,
} from '../hooks';
import { InviteLeaseDetailsSegmentEvents } from '../constants';
import type { InviteFlowMilestoneKey } from '../types';

interface HelpfulTipsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpfulTipsDialog = ({ isOpen, onClose }: HelpfulTipsDialogProps) => {
  return (
    <Dialog
      scroll="paper"
      open={isOpen}
      onClose={onClose}
      sx={{
        top: 'unset',
        '& .MuiBackdrop-root': {
          background: 'rgba(8, 10, 45, 0.4)',
        },
        '& .MuiDialogActions-root': {
          marginX: '16px !important',
        },
        '& .MuiPaper-root': {
          boxShadow: '4px 16px 32px 0px rgba(0, 0, 0, 0.08)',
          p: '32px',
          width: '508px',
          height: '442px',
          paddingX: '16px !important',
          [theme.breakpoints.down('md')]: {
            height: 'calc(100vh - 120px)',
            paddingX: '0px !important',
            paddingBottom: '16px !important',
          },
        },
        [theme.breakpoints.up('md')]: {
          top: '10px',
          marginLeft: '72px',
        },
      }}
      showCloseButton={false}
    >
      <DialogTitle sx={{ paddingX: '16px !important' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography
            variant="h5"
            component="h2"
            sx={{
              lineHeight: '36px',
              [theme.breakpoints.down('md')]: {
                fontSize: '24px',
              },
            }}
          >
            Tips from our team on selecting your coverage
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close helpful tips modal"
            sx={{ marginTop: '-8px', marginRight: '-8px' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          borderTop: 'none',
          marginBottom: '16px !important',
          overflowX: 'wrap',
          overflowY: 'scroll',
          paddingRight: '8px',
          paddingBottom: '16px',
          paddingX: '16px !important',
          gap: 1,
          scrollbarColor: `${theme.palette.neutralGrey[300]} white`,
          borderBottom: `1px solid ${theme.palette.amber?.[200] || '#d7d1c4'}`,
          [theme.breakpoints.up('md')]: {
            border: 'none',
            marginRight: '-12px !important',
            paddingRight: '8px',
            height: 294,
          },
        }}
      >
        <Stack gap={1} sx={{ [theme.breakpoints.up('md')]: { paddingRight: '8px' } }}>
          <Typography
            variant="body1"
            sx={{ lineHeight: '24px', fontWeight: 600, paddingTop: '8px' }}
          >
            Determining the right coverage length
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '8px' }}>
            Depending on your risk tolerance, our team recommends you to select the amount of
            coverage that protects you through your typical eviction timeline. You can use this
            coverage for months of rent loss at any point in the lease.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: '24px', fontWeight: 600 }}>
            Estimating the cost to your renter
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '8px' }}>
            Pricing varies based on a number of factors, but averages based on the months of
            coverage selected are:
            <ul
              style={{
                paddingLeft: '1rem',
                margin: 4,
                marginBottom: 8,
              }}
            >
              <li>
                <b style={{ fontWeight: 600 }}>3 months:</b> 75% of one month's rent
              </li>
              <li>
                <b style={{ fontWeight: 600 }}>6 months:</b> 100% of one month's rent
              </li>
              <li>
                <b style={{ fontWeight: 600 }}>12 months:</b> 115% of one month's rent
              </li>
            </ul>
            Your renter pays for the coverage. If you would like to offset the cost for your renter
            and pay a portion or the total cost of coverage yourself, we recommend talking to your
            renter about reimbursement.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: '24px', fontWeight: 600 }}>
            What Rent Coverage includes
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '8px' }}>
            Protection from losses due to unpaid rent.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: '24px', fontWeight: 600 }}>
            Adding optional Deposit Coverage
          </Typography>
          <Typography variant="body1">
            You have the option to allocate up to 1 month of coverage towards deposit related
            losses. Including Deposit Coverage will raise the renter cost of coverage by 30% on
            average and will reduce their chances of being approved after submitting an application
            with us.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" size="large" onClick={onClose} fullWidth>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface InviteLeaseDetailsDrawerScreeningResultsProps {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
  setValue: UseFormSetValue<Renter>;
  isSmbInvite: boolean;
  coverageLengthOptions: RadioInputBox[];
  newCoveragesEnabled?: boolean;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
  showCoverageSkeleton?: boolean;
}

export const InviteLeaseDetailsDrawerScreeningResults = ({
  control,
  errors,
  watch,
  setValue,
  isSmbInvite,
  coverageLengthOptions,
  newCoveragesEnabled = false,
  onMilestoneReached,
  showCoverageSkeleton = false,
}: InviteLeaseDetailsDrawerScreeningResultsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const includeDepositCoverage = watch('includeDepositCoverage');
  const customCoverage = watch('customCoverage');
  const { trackInvite } = useInviteTracking();
  const { user } = useCurrentUser();
  const isPmi = user && checkIfPmi(user);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Dynamic coverage rules logic for institutional (matches ScreeningResults.tsx)
  const address = useWatch({ control, name: 'address' });
  const screeningResult = useWatch({ control, name: 'approvalStatus' });
  const leaseStart = useWatch({ control, name: 'leaseStart' });
  const leaseEnd = useWatch({ control, name: 'leaseEnd' });
  const grossMonthlyRent = useWatch({ control, name: 'grossMonthlyRent' });
  const prepaidRent = useWatch({ control, name: 'prepaidRent' });
  const freeRent = useWatch({ control, name: 'freeRent' });

  const addressSelected = Boolean(address?.monolith_building_id);

  const isDynamicCoverageRulesEnabled = Boolean(address?.property_id && !isSmbInvite);

  const {
    data: coverageRulesFromApi,
    isLoading,
    isError,
    error,
  } = useDynamicCoverageRules({
    propertyId: address?.property_id,
    leaseStart,
    leaseEnd,
    grossMonthlyRent,
    prepaidRent,
    freeRent,
    enabled: isDynamicCoverageRulesEnabled,
  });
  const hasCustomScreeningStatusLabels = !isSmbInvite && (coverageRulesFromApi?.length ?? 0) > 0;
  const hasPricingCoverageRules = (coverageRulesFromApi ?? []).some((rule) =>
    Boolean(rule.pricing),
  );

  const showSkeleton = isLoading && !isSmbInvite && addressSelected;
  const isLocked = !addressSelected && !isSmbInvite;

  const options = useMemo(() => {
    if (isLocked) {
      return buildEmptyOptions(staticInstitutionalScreeningResultsOptions.length);
    }
    return isSmbInvite
      ? staticSmbScreeningResultsOptions
      : resolveDynamicCoverageOptions(
          staticInstitutionalScreeningResultsOptions,
          coverageRulesFromApi,
        );
  }, [coverageRulesFromApi, isSmbInvite, isLocked]);

  useEffect(() => {
    const selectedOption = options.find((opt) => opt.value === screeningResult);

    if (
      !isSmbInvite &&
      !isLoading &&
      screeningResult &&
      (!selectedOption || selectedOption.disabled)
    ) {
      setValue('approvalStatus', '', { shouldValidate: true });
    }
  }, [isSmbInvite, isLoading, screeningResult, options, setValue]);

  useTrackScreeningLabelsLoaded({
    propertyId: address?.property_id ?? address?.monolith_building_id,
    isLoading,
    isError,
    error,
    hasCustomScreeningStatusLabels,
    labelCount: (isSmbInvite ? staticSmbScreeningResultsOptions : options).length,
    enabled: !hasPricingCoverageRules,
  });

  useTrackCoveragePricingResolved({
    propertyId: address?.property_id,
    enabled: !isSmbInvite && hasPricingCoverageRules,
    isLoading,
    isError,
    error,
    hasCustomScreeningStatusLabels,
    pricingOptionCount: coverageRulesFromApi?.length ?? 0,
    pricingData: coverageRulesFromApi,
  });

  return (
    <>
      {isSmbInvite && (
        <>
          <Typography
            variant="h6"
            component="h2"
            mb={1}
            fontWeight="bold !important"
            lineHeight="28px"
          >
            Select your maximum coverage limit
          </Typography>
          <Typography
            mb={2}
            variant="body2"
            fontWeight="350"
            letterSpacing={0}
            sx={{
              fontSize: '16px',
              [theme.breakpoints.down('md')]: {
                fontSize: '14px',
                lineHeight: '22px',
              },
            }}
          >
            This corresponds to the maximum amount of rent loss you can claim. For example, if you
            select 3 months of coverage and your rent is $2,000, you can claim up to $6,000 of rent
            loss.
          </Typography>
          <Typography
            mb={4}
            variant="body2"
            fontWeight="350"
            letterSpacing={0}
            sx={{
              fontSize: '16px',
              [theme.breakpoints.down('md')]: {
                fontSize: '14px',
                lineHeight: '22px',
              },
            }}
          >
            If you need help deciding,{' '}
            <Link
              onClick={openModal}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                cursor: 'pointer',
                borderBottom: `2px solid ${theme.palette.midnightGrey[700]}`,
                '&:hover': {
                  color: `${theme.palette.midnightGrey[950]} !important`,
                  borderColor: `${theme.palette.midnightGrey[950]} !important`,
                },
              }}
            >
              review our helpful tips.
            </Link>
          </Typography>

          <Typography
            mt={4}
            variant="body2"
            fontWeight="350"
            mb="12px"
            letterSpacing={0}
            sx={{
              fontSize: '16px',
              [theme.breakpoints.down('md')]: {
                fontSize: '14px',
                lineHeight: '22px',
              },
            }}
          >
            Select one{' '}
            <Box component="span" sx={{ color: theme.palette.red[600] }}>
              *
            </Box>
          </Typography>
          <Box mt={2} mb={4}>
            {newCoveragesEnabled && showCoverageSkeleton ? (
              <GroupRadioButtonV2Skeleton dataTestId="monthsOfCoverage-skeleton" />
            ) : newCoveragesEnabled ? (
              <Controller
                control={control}
                name="monthsOfCoverage"
                render={({ field }) => (
                  <GroupRadioButtonV2
                    id="monthsOfCoverage"
                    dataTestId="monthsOfCoverage"
                    options={coverageLengthOptions}
                    field={{
                      ...field,
                      onChange: (_event: any, selectedValue: string) => {
                        field.onChange(selectedValue);
                        onMilestoneReached?.('hadLabelSelected');
                        trackInvite(InviteLeaseDetailsSegmentEvents.ScreeningLabelSelected, {
                          property_id: address?.property_id,
                          address_id: address?.address_id,
                          has_custom_screening_status_labels: false,
                          screening_label_id: selectedValue,
                          mapped_outcome: selectedValue,
                        });
                      },
                    }}
                    useDrawerStyles
                  />
                )}
              />
            ) : (
              <Controller
                control={control}
                name="approvalStatus"
                render={({ field }) => (
                  <GroupRadioButtonV2
                    id="approvalStatus"
                    dataTestId="approvalStatus"
                    options={staticSmbScreeningResultsOptions}
                    field={{
                      ...field,
                      onChange: (_event: any, selectedValue: string) => {
                        field.onChange(selectedValue);
                        onMilestoneReached?.('hadLabelSelected');
                        trackInvite(InviteLeaseDetailsSegmentEvents.ScreeningLabelSelected, {
                          property_id: address?.property_id,
                          address_id: address?.address_id,
                          has_custom_screening_status_labels: false,
                          screening_label_id: selectedValue,
                          mapped_outcome: selectedValue,
                        });
                      },
                    }}
                    useDrawerStyles
                    layout={isPmi ? 'stacked' : undefined}
                    disabled={!!isPmi && !!customCoverage}
                  />
                )}
              />
            )}
            {newCoveragesEnabled && errors.monthsOfCoverage && (
              <FormHelperText error={true}>{errors.monthsOfCoverage.message}</FormHelperText>
            )}
            {!newCoveragesEnabled && errors.approvalStatus && (
              <FormHelperText error={true}>{errors.approvalStatus.message}</FormHelperText>
            )}
          </Box>
        </>
      )}

      {!isSmbInvite && (
        <>
          <Typography
            variant="h6"
            component="h2"
            mb={1}
            fontWeight="bold !important"
            sx={{
              lineHeight: '28px',
              [theme.breakpoints.down('md')]: {
                fontSize: '16px',
                lineHeight: '24px',
              },
            }}
          >
            Screening result
          </Typography>
          <Typography
            variant="body2"
            fontWeight="350"
            letterSpacing={0}
            sx={{
              fontSize: '16px',
              [theme.breakpoints.down('md')]: {
                fontSize: '14px',
                lineHeight: '22px',
              },
            }}
          >
            Based on your review of this applicant at this time, select the appropriate screening
            result. This can be changed later, but it will impact the quoted premium.
          </Typography>
          {isLocked && (
            <InfoBanner
              icon={<LockOutlinedIcon fontSize="inherit" />}
              sx={{
                mt: 1,
                '.MuiTypography-root': {
                  textAlign: 'left',
                  [theme.breakpoints.down('md')]: {
                    fontSize: '14px',
                    lineHeight: '22px',
                    fontWeight: 400,
                  },
                },
              }}
            >
              Please complete the lease information above first
            </InfoBanner>
          )}
          <Typography
            mt={4}
            variant="body2"
            fontWeight="350"
            mb="12px"
            letterSpacing={0}
            sx={{
              fontSize: '16px',
              [theme.breakpoints.down('md')]: {
                fontSize: '14px',
                lineHeight: '22px',
              },
            }}
          >
            Select one{' '}
            <Box component="span" sx={{ color: theme.palette.red[600] }}>
              *
            </Box>
          </Typography>
          {showSkeleton ? (
            <GroupRadioButtonV2Skeleton
              count={staticInstitutionalScreeningResultsOptions.length}
              dataTestId="screening-results-skeleton"
            />
          ) : (
            <Controller
              control={control}
              name="approvalStatus"
              render={({ field }) => (
                <GroupRadioButtonV2
                  id="approvalStatus"
                  dataTestId="approvalStatus"
                  options={options.map((opt) => ({
                    id: opt.id,
                    value: opt.value,
                    text: opt.text,
                    subText: opt.subText,
                    disabled: opt.disabled,
                    tooltip: opt.tooltip,
                    disabledTooltip: opt.disabledTooltip,
                  }))}
                  field={{
                    ...field,
                    onChange: (_event: any, selectedValue: string) => {
                      field.onChange(selectedValue);
                      onMilestoneReached?.('hadLabelSelected');
                      trackInvite(InviteLeaseDetailsSegmentEvents.ScreeningLabelSelected, {
                        property_id: address?.property_id,
                        address_id: address?.address_id,
                        has_custom_screening_status_labels: hasCustomScreeningStatusLabels,
                        screening_label_id: selectedValue,
                        mapped_outcome: selectedValue,
                      });
                    },
                  }}
                  disabled={isLocked}
                  useDrawerStyles
                  layout="stacked"
                />
              )}
            />
          )}
          {errors.approvalStatus && (
            <FormHelperText error={true}>{errors.approvalStatus.message}</FormHelperText>
          )}
        </>
      )}
      {isSmbInvite && !isPmi && (
        <>
          <Box mb={2} mt={1}>
            <Controller
              name="includeDepositCoverage"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value ? true : false}
                      data-testid="include-deposit-coverage"
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        color: `${field.value ? theme.palette.midnightGrey[950] : '#888888'} !important`,
                        fontSize: '16px',
                        [theme.breakpoints.down('md')]: {
                          fontSize: '14px',
                          lineHeight: '22px',
                        },
                      }}
                    >
                      For an additional fee, add optional coverage to replace your cash security
                      deposit.
                    </Typography>
                  }
                />
              )}
            />
          </Box>
          <Box mb={4}>
            {includeDepositCoverage && (
              <Alert severity="warning">
                <Typography
                  variant="body2"
                  mb={1}
                  sx={{
                    fontSize: '16px',
                    [theme.breakpoints.down('md')]: {
                      fontSize: '14px',
                      lineHeight: '22px',
                    },
                  }}
                >
                  Please note the following if you select this option:
                </Typography>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 3 }}>
                  <li>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '16px',
                        [theme.breakpoints.down('md')]: {
                          fontSize: '14px',
                          lineHeight: '22px',
                        },
                      }}
                    >
                      Deposit Coverage is intended for landlords who do not expect to hold a cash
                      security deposit
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '16px',
                        [theme.breakpoints.down('md')]: {
                          fontSize: '14px',
                          lineHeight: '22px',
                        },
                      }}
                    >
                      The added coverage may raise renter's quote by up to 30%
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '16px',
                        [theme.breakpoints.down('md')]: {
                          fontSize: '14px',
                          lineHeight: '22px',
                        },
                      }}
                    >
                      The likelihood of a renter being approved may be lower
                    </Typography>
                  </li>
                </Box>
              </Alert>
            )}
          </Box>
        </>
      )}
      {isSmbInvite && isPmi && (
        <>
          <Box my={4}>
            <Controller
              name="customCoverage"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      color="primary"
                      sx={{ mx: 1 }}
                      onChange={(_, checked) => {
                        field.onChange(checked);
                        if (!checked) return;
                        trackInvite(InviteLeaseDetailsSegmentEvents.RentCoverageSelected, {
                          property_id: address?.property_id,
                          address_id: address?.address_id,
                          value: RentCoverageTypes.Customize,
                        });
                        trackInvite(InviteLeaseDetailsSegmentEvents.DepositCoverageSelected, {
                          property_id: address?.property_id,
                          address_id: address?.address_id,
                          value: SecurityDepositTypes.CustomizeOrRemove,
                        });
                      }}
                    />
                  }
                  label="I want to customize my own coverage"
                />
              )}
            />
          </Box>
          <Box mb={4}>
            <Typography variant="helpertext">
              * Policy price paid by the renter is determined by gross rent, the length of coverage,
              and a renter's risk profile, decided by the landlord.
            </Typography>
          </Box>
        </>
      )}

      <HelpfulTipsDialog isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
};
